import { useEffect, useState, useCallback, useRef } from "react";
import { Users, Lock, MessageCircle, Phone, GripVertical, Trash2, Pencil, Plus, Tags, MessagesSquare, StickyNote, CalendarClock, Search } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { rupee } from "@/lib/format";
import PaymentModal from "@/components/PaymentModal";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function SellerLeadsCRM() {
  const [status, setStatus] = useState(null);
  const [payment, setPayment] = useState(null);
  const [payOpen, setPayOpen] = useState(false);

  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/seller/crm/status"); setStatus(data); }
    catch (e) { toast.error(apiError(e)); }
  }, []);
  useEffect(() => { loadStatus(); }, [loadStatus]);

  const subscribe = async (planId) => {
    try { const { data } = await api.post("/seller/crm/subscribe", { package_id: planId }); setPayment(data); setPayOpen(true); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!status) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2"><Users className="h-6 w-6 text-primary" /> Leads CRM</h1>
        <p className="text-muted-foreground text-sm font-medium">Every buyer who taps WhatsApp on your listings becomes a lead. Follow up, move them across stages and close deals.</p>
      </div>

      {status.active ? (
        <CrmBoard status={status} />
      ) : (
        <LockedTeaser status={status} onSubscribe={subscribe} />
      )}

      <PaymentModal payment={payment} open={payOpen} onClose={() => setPayOpen(false)} onDone={loadStatus} />
    </div>
  );
}

function LockedTeaser({ status, onSubscribe }) {
  return (
    <div className="space-y-5">
      <div data-testid="crm-locked-hero" className="relative overflow-hidden bg-slate-900 text-white rounded-2xl p-8">
        <div className="absolute inset-0 opacity-10" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #f97316 0, transparent 40%)" }} />
        <div className="relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-bold"><Lock className="h-3.5 w-3.5" /> Locked</div>
          <div className="mt-4 font-heading text-4xl font-extrabold">
            You have <span className="text-primary" data-testid="crm-buyer-count">{status.buyer_count}</span> buyer {status.buyer_count === 1 ? "connection" : "connections"} waiting
          </div>
          <p className="text-white/70 mt-2 max-w-lg font-medium">Unlock the Leads CRM to see every buyer's mobile number, message them on WhatsApp in one tap, organise them into follow-up stages and use ready message templates to close more deals.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {status.plans.map((p) => (
          <div key={p.id} data-testid={`crm-plan-${p.id}`} className="border-2 rounded-xl p-5 flex flex-col">
            <div className="font-heading font-extrabold text-primary uppercase text-sm tracking-wide">{p.name || "Leads CRM"}</div>
            <div className="font-heading text-3xl font-extrabold mt-1">{rupee(p.price)}</div>
            <div className="text-xs text-muted-foreground">+ GST · valid 1 year</div>
            {p.features && <div className="text-xs font-semibold mt-2">{p.features}</div>}
            <Button data-testid={`crm-subscribe-${p.id}`} onClick={() => onSubscribe(p.id)} className="w-full font-bold mt-4">Subscribe · {rupee(Math.round(p.price * 1.18))}</Button>
          </div>
        ))}
        {status.plans.length === 0 && <div className="text-sm text-muted-foreground font-semibold border rounded-xl p-4 col-span-full">Leads CRM plans are being set up. Please check back shortly.</div>}
      </div>
    </div>
  );
}

const fillTpl = (tpl, mobile) => (tpl || "Hi, thanks for your interest in our stock on Cleartile!").replaceAll("{company}", "").replaceAll("{city}", "").replaceAll("{mobile}", mobile || "");

function CrmBoard({ status }) {
  const [labels, setLabels] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [managerOpen, setManagerOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [noteLead, setNoteLead] = useState(null);
  const [query, setQuery] = useState("");
  const dragRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);

  const load = useCallback(async () => {
    try {
      const [lbl, bys] = await Promise.all([api.get("/seller/crm/labels"), api.get("/seller/crm/buyers")]);
      setLabels(lbl.data); setBuyers(bys.data);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = async (id, labelId) => {
    const b = buyers.find((x) => x.id === id);
    if (!b || b.label_id === labelId) return;
    setBuyers((cur) => cur.map((x) => (x.id === id ? { ...x, label_id: labelId } : x)));
    try { await api.patch(`/seller/crm/buyers/${id}/move`, { label_id: labelId }); }
    catch (e) { toast.error(apiError(e)); load(); }
  };

  const tpl = (labelId) => labels.find((l) => l.id === labelId)?.message || "";
  const wa = (b) => `https://wa.me/91${String(b.mobile).replace(/\D/g, "").slice(-10)}?text=${encodeURIComponent(fillTpl(tpl(b.label_id), b.mobile))}`;

  const q = query.trim().toLowerCase();
  const filtered = buyers.filter((b) => !q || String(b.mobile).includes(q) || (b.product_titles || []).some((t) => String(t).toLowerCase().includes(q)));
  const byLabel = (id) => filtered.filter((b) => b.label_id === id);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Badge className="bg-emerald-100 text-emerald-800 border border-emerald-300 font-bold" data-testid="crm-active-badge">CRM Active{status.expires_at ? ` · till ${status.expires_at.slice(0, 10)}` : ""}</Badge>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input data-testid="crm-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search buyer / product…" className="pl-9 h-10 w-56" />
          </div>
          <Button data-testid="crm-templates-btn" onClick={() => setTemplatesOpen(true)} variant="outline" className="font-bold gap-1"><MessagesSquare className="h-4 w-4" /> Templates</Button>
          <Button data-testid="crm-labels-btn" onClick={() => setManagerOpen(true)} variant="outline" className="font-bold gap-1"><Tags className="h-4 w-4" /> Labels</Button>
        </div>
      </div>

      {loading ? <div className="text-center text-muted-foreground py-16">Loading buyers…</div> : buyers.length === 0 ? (
        <div className="text-center py-16 border rounded-2xl">
          <Users className="h-10 w-10 text-muted-foreground/40 mx-auto" />
          <p className="font-bold mt-3">No buyer connections yet</p>
          <p className="text-sm text-muted-foreground">When buyers tap WhatsApp on your listings, they'll appear here for follow-up.</p>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto ct-scroll pb-4" data-testid="crm-board">
          {labels.map((label) => {
            const items = byLabel(label.id);
            const over = overCol === label.id;
            return (
              <div key={label.id} data-testid={`crm-column-${label.id}`}
                onDragOver={(e) => { e.preventDefault(); if (dragRef.current) setOverCol(label.id); }}
                onDragLeave={() => setOverCol((c) => (c === label.id ? null : c))}
                onDrop={() => { const id = dragRef.current; if (id) move(id, label.id); dragRef.current = null; setDragId(null); setOverCol(null); }}
                className={`w-72 shrink-0 rounded-2xl border transition-colors ${over ? "bg-primary/10 border-primary border-dashed" : "bg-secondary/50"}`}>
                <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderTopColor: label.color, borderTopWidth: 3 }}>
                  <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full" style={{ background: label.color }} /><span className="font-heading font-extrabold text-sm">{label.name}</span></div>
                  <Badge variant="secondary" className="font-bold" data-testid={`crm-count-${label.id}`}>{items.length}</Badge>
                </div>
                <div className="p-3 space-y-3 min-h-[120px]">
                  {items.map((b) => (
                    <div key={b.id} data-testid={`crm-card-${b.id}`}
                      draggable onDragStart={() => { dragRef.current = b.id; setDragId(b.id); }} onDragEnd={() => { dragRef.current = null; setDragId(null); setOverCol(null); }}
                      className={`bg-card border rounded-xl p-3 shadow-sm cursor-grab active:cursor-grabbing ${dragId === b.id ? "opacity-40" : ""}`}>
                      <div className="flex items-start gap-1.5">
                        <GripVertical className="h-4 w-4 text-muted-foreground/50 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <div className="font-bold text-sm flex items-center gap-1"><Phone className="h-3.5 w-3.5 text-primary" /> {b.mobile}</div>
                          <div className="text-xs text-muted-foreground">{b.clicks} WhatsApp {b.clicks === 1 ? "click" : "clicks"}</div>
                        </div>
                      </div>
                      {b.product_titles?.length > 0 && <div className="mt-2 text-[11px] text-muted-foreground line-clamp-2">Interested: {b.product_titles.join(", ")}</div>}
                      {b.follow_up && <Badge data-testid={`crm-followup-${b.id}`} className="mt-2 bg-amber-100 text-amber-800 border-amber-300 border text-[10px] gap-1"><CalendarClock className="h-3 w-3" /> {b.follow_up}</Badge>}
                      {b.note && <div className="mt-2 text-xs bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5 text-amber-900 line-clamp-2">{b.note}</div>}
                      <div className="flex items-center gap-1.5 mt-3">
                        <a data-testid={`crm-whatsapp-${b.id}`} href={wa(b)} target="_blank" rel="noreferrer" className="grid place-items-center h-8 w-8 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 shrink-0"><MessageCircle className="h-4 w-4" /></a>
                        <button data-testid={`crm-note-${b.id}`} onClick={() => setNoteLead(b)} className={`grid place-items-center h-8 w-8 rounded-lg border shrink-0 ${b.note || b.follow_up ? "bg-amber-100 border-amber-300 text-amber-700" : "hover:bg-secondary text-muted-foreground"}`}><StickyNote className="h-4 w-4" /></button>
                        <Select value={b.label_id} onValueChange={(v) => move(b.id, v)}>
                          <SelectTrigger data-testid={`crm-move-${b.id}`} className="h-8 text-xs font-semibold flex-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{labels.map((l) => <SelectItem key={l.id} value={l.id} className="text-xs">{l.name}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  ))}
                  {items.length === 0 && <div className="text-center text-xs text-muted-foreground py-6 font-medium">{over ? "Drop here" : "No leads"}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LabelManager open={managerOpen} onClose={() => setManagerOpen(false)} labels={labels} onChanged={load} />
      <TemplatePlanner open={templatesOpen} onClose={() => setTemplatesOpen(false)} labels={labels} onChanged={load} />
      <NoteDialog lead={noteLead} onClose={() => setNoteLead(null)} onSaved={(u) => { setBuyers((c) => c.map((x) => (x.id === u.id ? { ...x, ...u } : x))); setNoteLead(null); }} />
    </div>
  );
}

function NoteDialog({ lead, onClose, onSaved }) {
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState("");
  useEffect(() => { if (lead) { setNote(lead.note || ""); setFollowUp(lead.follow_up || ""); } }, [lead]);
  const save = async () => {
    try { await api.patch(`/seller/crm/buyers/${lead.id}/note`, { note, follow_up: followUp || null }); toast.success("Saved"); onSaved({ id: lead.id, note, follow_up: followUp || null }); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="crm-note-dialog">
        <DialogHeader><DialogTitle className="font-heading">Buyer Note</DialogTitle><DialogDescription className="text-xs">{lead?.mobile} · jot a note or set a follow-up date.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="text-sm font-bold">Note</label><Textarea data-testid="crm-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={4} placeholder="e.g. Wants 200 boxes, negotiating price…" /></div>
          <div><label className="text-sm font-bold flex items-center gap-1"><CalendarClock className="h-4 w-4" /> Next follow-up</label><Input data-testid="crm-followup-input" type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} className="h-10" /></div>
          <Button data-testid="crm-save-note" onClick={save} className="w-full font-bold h-11">Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TemplatePlanner({ open, onClose, labels, onChanged }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) setRows(labels.map((l) => ({ ...l, message: l.message || "" }))); }, [open, labels]);
  const save = async (row) => {
    try { await api.put(`/seller/crm/labels/${row.id}`, { name: row.name, color: row.color, message: row.message }); toast.success("Template saved"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="crm-template-dialog">
        <DialogHeader><DialogTitle className="font-heading">WhatsApp Message Templates</DialogTitle><DialogDescription className="text-xs">Set the message sent to a buyer at each stage. Placeholder: {"{mobile}"}.</DialogDescription></DialogHeader>
        <div className="space-y-3 max-h-[55vh] overflow-y-auto ct-scroll">
          {rows.map((row, idx) => (
            <div key={row.id} data-testid={`crm-template-row-${row.id}`} className="border rounded-xl p-3">
              <div className="flex items-center gap-2 mb-2"><span className="h-3 w-3 rounded-full" style={{ background: row.color }} /><span className="font-heading font-extrabold text-sm">{row.name}</span></div>
              <Textarea data-testid={`crm-template-input-${row.id}`} value={row.message} onChange={(e) => setRows((c) => c.map((r, i) => i === idx ? { ...r, message: e.target.value } : r))} rows={3} />
              <div className="flex justify-end mt-2"><Button size="sm" data-testid={`crm-save-template-${row.id}`} onClick={() => save(rows[idx])} className="font-bold">Save</Button></div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LabelManager({ open, onClose, labels, onChanged }) {
  const [rows, setRows] = useState([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#64748b");
  const [dragIdx, setDragIdx] = useState(null);
  useEffect(() => { if (open) setRows(labels.map((l) => ({ ...l }))); }, [open, labels]);

  const saveRow = async (row) => { try { await api.put(`/seller/crm/labels/${row.id}`, { name: row.name, color: row.color, message: row.message || "" }); toast.success("Saved"); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const add = async () => { if (!newName.trim()) { toast.error("Enter a name"); return; } try { await api.post("/seller/crm/labels", { name: newName.trim(), color: newColor }); setNewName(""); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { if (!window.confirm("Delete this label? Its buyers move to the first label.")) return; try { await api.delete(`/seller/crm/labels/${id}`); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const onDrop = async (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); return; }
    const next = [...rows]; const [m] = next.splice(dragIdx, 1); next.splice(idx, 0, m); setRows(next); setDragIdx(null);
    try { await api.put("/seller/crm/labels/reorder", { ids: next.map((r) => r.id) }); onChanged(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="crm-label-dialog">
        <DialogHeader><DialogTitle className="font-heading">Manage Labels</DialogTitle><DialogDescription className="text-xs">Drag the handle to reorder your follow-up stages.</DialogDescription></DialogHeader>
        <div className="space-y-2 max-h-[50vh] overflow-y-auto ct-scroll">
          {rows.map((row, idx) => (
            <div key={row.id} data-testid={`crm-label-row-${row.id}`} draggable onDragStart={() => setDragIdx(idx)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(idx)}
              className={`flex items-center gap-2 border rounded-xl px-2 py-2 bg-card ${dragIdx === idx ? "opacity-50" : ""}`}>
              <span data-testid={`crm-label-drag-${row.id}`} className="cursor-grab active:cursor-grabbing text-muted-foreground"><GripVertical className="h-5 w-5" /></span>
              <input type="color" value={row.color} onChange={(e) => setRows((c) => c.map((r, i) => i === idx ? { ...r, color: e.target.value } : r))} className="h-8 w-9 rounded cursor-pointer border" />
              <Input data-testid={`crm-label-name-${row.id}`} value={row.name} onChange={(e) => setRows((c) => c.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))} className="h-9" />
              <Button size="sm" variant="outline" data-testid={`crm-label-save-${row.id}`} onClick={() => saveRow(rows[idx])}><Pencil className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" data-testid={`crm-label-del-${row.id}`} onClick={() => remove(row.id)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          ))}
        </div>
        <div className="border-t pt-4">
          <div className="text-sm font-bold mb-2">Add a new label</div>
          <div className="flex items-center gap-2">
            <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)} className="h-9 w-10 rounded cursor-pointer border" />
            <Input data-testid="crm-new-label-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Site Visit" className="h-9" />
            <Button data-testid="crm-add-label" onClick={add} className="font-bold gap-1"><Plus className="h-4 w-4" /> Add</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
